import { Button, cn } from "@tellescope/design-system";
import { Activity, ArrowLeft, PencilRuler } from "lucide-react";
import { BlockProps } from "../../../types";
import { useVitalsBlock } from "../../../hooks";
import { BlockHeader } from "../BlockHeader";
import { BlockContent } from "../BlockContent";
import { VitalsRecordsList } from "./VitalsRecordsList";
import { VitalsProgramSubsection } from "./VitalsProgramSubsection";
import { AddBPReadingSubsection } from "./AddBPReadingSubsection";
import { AddOtherReadingSubsection } from "./AddOtherReadingSubsection";
import { useTranslation } from "react-i18next";
import { BlockEmptyState } from "../BlockEmptyState";

export interface VitalsBlockProps extends BlockProps {
  /** Whether the current user may invalidate / review observations. See {@link BlockProps}. */
  canUpdateObservations: boolean;
}

export const VitalsBlock = ({
  onMaximize,
  alwaysShowButtons = false,
  className,
  contentClassName,
  canUpdateObservations,
  ...rest
}: VitalsBlockProps) => {
  const { t } = useTranslation("vitals");
  const {
    parsedInfo,
    enduser,
    activeSubsection,
    subtypeMetadata,
    openSubtype,
    openAddReading,
    closeSubsection,
    setObservationId,
    isEditingTitle,
    setIsEditingTitle,
    newTitle,
    setNewTitle,
    editMode,
    setEditMode,
    updateInfo,
    duplicateBlock,
    deleteBlock,
    toggleExpanded,
    expanded,
    blockLayout,
    canEditTitle,
    canCreateBlock,
    canDeleteBlock,
  } = useVitalsBlock({ ...rest, onMaximize, alwaysShowButtons, className });

  return (
    <div className={cn("group", className)}>
      <BlockHeader
        editMode={editMode}
        setEditMode={setEditMode}
        isEditingTitle={isEditingTitle}
        setIsEditingTitle={setIsEditingTitle}
        newTitle={newTitle}
        setNewTitle={setNewTitle}
        canEditTitle={canEditTitle}
        parsedInfo={parsedInfo}
        updateInfo={updateInfo}
        duplicateBlock={duplicateBlock}
        deleteBlock={deleteBlock}
        canCreateBlock={canCreateBlock}
        canDeleteBlock={canDeleteBlock}
        onMaximize={onMaximize}
        alwaysShowButtons={alwaysShowButtons}
        actions={
          editMode &&
          !activeSubsection && (
            <>
              <Button
                iconOnly
                variant="ghost"
                size="sm"
                color="muted"
                hoverLabel={t("Add BP Reading")}
                onClick={() => openAddReading("bp")}
              >
                <Activity />
              </Button>
              <Button
                iconOnly
                variant="ghost"
                size="sm"
                color="muted"
                hoverLabel={t("Add Other Reading")}
                onClick={() => openAddReading("other")}
              >
                <PencilRuler />
              </Button>
            </>
          )
        }
      />

      <BlockContent
        editMode={editMode}
        setEditMode={setEditMode}
        expanded={expanded}
        layout={blockLayout}
        onToggleExpanded={() => toggleExpanded()}
        className={cn("flex flex-col", contentClassName)}
      >
        {!enduser ? (
          <div className="flex flex-1 items-center justify-center">
            <BlockEmptyState>{t("Loading vitals...")}</BlockEmptyState>
          </div>
        ) : activeSubsection === "add-bp-reading" ? (
          <AddBPReadingSubsection enduserId={enduser.id} onSubmit={closeSubsection} onCancel={closeSubsection} />
        ) : activeSubsection === "add-other-reading" ? (
          <AddOtherReadingSubsection enduserId={enduser.id} onSubmit={closeSubsection} onCancel={closeSubsection} />
        ) : activeSubsection === "vitals-subtype" && subtypeMetadata ? (
          <div className="flex flex-col flex-1 min-h-0 gap-2">
            <Button
              variant="link"
              color="secondary"
              size="sm"
              className="h-auto self-start p-0"
              onClick={closeSubsection}
            >
              <ArrowLeft />
              {t("All Vitals")}
            </Button>
            <VitalsProgramSubsection
              enduserId={enduser.id}
              observationType={subtypeMetadata.observationType}
              observationId={subtypeMetadata.observationId}
              setObservationId={setObservationId}
              canUpdateObservations={canUpdateObservations}
              chartHeight="20svh"
            />
          </div>
        ) : (
          <VitalsRecordsList
            enduserId={enduser.id}
            showFilters={expanded}
            hideButtons
            canUpdateObservations={canUpdateObservations}
            onItemClick={(observationId, observationType, title) =>
              openSubtype({ observationId, observationType, title })
            }
            onAddReading={openAddReading}
          />
        )}
      </BlockContent>
    </div>
  );
};
